'use client'

import React, { useState } from 'react'

import { Box, chakra } from '@chakra-ui/react'

import {
  fieldLabelStyles,
  fieldLabelWrapperStyles,
} from '../shared/fieldLabelStyles'
import {
  ColorScheme,
  actionButtonWrapperStyles,
  containerStyles,
  counterTextStyles,
  inputRowStyles,
  inputStyles,
  warningTextStyles,
} from './TextInput.styles'

export type TextInputProps = {
  value: string
  /** DOM change handler. Optional if `onValueChange` is provided. */
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  /** Convenience change handler receiving the input value directly. */
  onValueChange?: (value: string) => void
  onSubmit?: () => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  actionButton?: React.ReactNode
  leftIcon?: React.ReactNode
  placeholder?: string
  /** Optional uppercase label rendered above the input. */
  label?: string
  colorScheme?: ColorScheme
  width?: string | number
  height?: string | number
  warningText?: string
  hasWarning?: boolean
  bgColor?: string
  maxLength?: number
  autoFocus?: boolean
  disabled?: boolean
  'data-testid'?: string
  /**
   * Extra attributes forwarded to the underlying `<input>` (e.g. ARIA combobox
   * wiring). The component's own controlled props (value/onChange/disabled/…)
   * always take precedence.
   */
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

export const TextInput = ({
  value,
  onChange,
  onValueChange,
  placeholder = 'Search…',
  label,
  colorScheme = 'dark',
  width = '200px',
  height = '36px',
  warningText,
  hasWarning = false,
  onSubmit,
  onBlur,
  onFocus,
  actionButton,
  leftIcon,
  bgColor,
  maxLength,
  autoFocus,
  disabled = false,
  'data-testid': dataTestId,
  inputProps,
}: TextInputProps) => {
  const [isFocused, setIsFocused] = useState(false)
  const heightPx = typeof height === 'number' ? `${height}px` : height

  const charsRemaining =
    maxLength !== undefined ? maxLength - value.length : undefined
  const isAtLimit = charsRemaining !== undefined && charsRemaining <= 0
  const isNearLimit = charsRemaining !== undefined && charsRemaining <= 10

  const isShowWarning = hasWarning || isAtLimit
  const activeWarningText = hasWarning
    ? warningText
    : `${maxLength} character limit`

  const input = (
    <Box
      {...containerStyles(
        colorScheme,
        isFocused,
        isShowWarning,
        heightPx,
        width,
        bgColor,
        disabled
      )}
    >
      {isShowWarning && (
        <Box {...warningTextStyles(colorScheme)}>{activeWarningText}</Box>
      )}
      <Box {...inputRowStyles} {...(actionButton ? { pr: '28px' } : {})}>
        {leftIcon}
        <chakra.input
          {...inputProps}
          autoFocus={autoFocus}
          data-testid={dataTestId}
          disabled={disabled}
          maxLength={maxLength}
          onBlur={(e) => {
            setIsFocused(false)
            onBlur?.(e)
          }}
          onChange={(e) => {
            onChange?.(e)
            onValueChange?.(e.target.value)
          }}
          onFocus={(e) => {
            setIsFocused(true)
            onFocus?.(e)
          }}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') onSubmit?.()
          }}
          placeholder={placeholder}
          value={value}
          {...inputStyles(colorScheme, disabled)}
        />
        {isNearLimit && maxLength && (
          <Box as='span' {...counterTextStyles(colorScheme)}>
            ({value.length}/{maxLength})
          </Box>
        )}
      </Box>
      {actionButton && <Box {...actionButtonWrapperStyles}>{actionButton}</Box>}
    </Box>
  )

  if (!label) return input

  return (
    <Box {...fieldLabelWrapperStyles(width)}>
      <Box as='label' {...fieldLabelStyles(colorScheme)}>
        {label}
      </Box>
      {input}
    </Box>
  )
}
